import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, RefreshCw, Sliders, Palette, Sparkles, Eye, EyeOff } from 'lucide-react';
import { AppIcon, AIEditParams } from '../types';
import { VolcengineService } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { DownloadService } from '../services/download';
import { useAppStore } from '../stores/appStore';
import { getVolcengineApiKey } from '../config';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

interface AIIconEditorProps {
  icon: AppIcon | null;
  onClose: () => void;
  standalone?: boolean;
}

const AI_STYLES = [
  { value: 'modern', label: 'Modern', description: 'Clean modern design' },
  { value: 'cartoon', label: 'Cartoon', description: 'Cute cartoon effect' },
  { value: 'realistic', label: 'Realistic', description: 'Photorealistic effect' },
  { value: 'minimal', label: 'Minimal', description: 'Minimalist design' },
  { value: 'vintage', label: 'Vintage', description: 'Classic retro effect' },
];

const AI_PROMPTS = {
  enhance: 'Enhance details and clarity',
  simplify: 'Simplify design, remove complexity',
  colorize: 'Improve color palette and contrast',
  flatten: 'Apply flat, modern style',
  gradient: 'Add gradient for depth',
  outline: 'Add outline to improve recognition',
  '3d': 'Add 3D effect for depth',
  neon: 'Add neon glow for tech vibe',
};

export const AIIconEditor: React.FC<AIIconEditorProps> = ({ icon, onClose, standalone = false }) => {
  const location = useLocation();
  const navState = location.state as { icon?: AppIcon } | null;
  const { settings } = useAppStore();
  const [originalImage, setOriginalImage] = useState<string>(icon?.artworkUrl512 || navState?.icon?.artworkUrl512 || '');
  const [editedImage, setEditedImage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: string } | null>(null);
  const [comparisonMode, setComparisonMode] = useState<'side' | 'slider' | 'overlay'>('side');
  const [zoom, setZoom] = useState<number>(100);
  const [sliderPos, setSliderPos] = useState<number>(50);
  const [overlayOpacity, setOverlayOpacity] = useState<number>(0.5);
  const comparisonRef = useRef<HTMLDivElement>(null);
  const [aiParams, setAiParams] = useState<AIEditParams>({
    image: '',
    prompt: AI_STYLES[0].description,
    style: 'modern',
    strength: 0.7,
    outputFormat: 'png',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 将图片转换为Base64
  const convertImageToBase64 = useCallback(async (imageUrl: string) => {
    try {
      if (imageUrl.startsWith('data:')) {
        return imageUrl;
      }
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Image conversion failed:', error);
      throw new Error('Image conversion failed');
    }
  }, []);

  // AI处理图标
  const handleAIProcess = async () => {
    const authUser = (useAuthStore.getState().user as any) || null;
    if (!authUser) {
      toast.error('Please sign in first');
      return;
    }
    if (!getVolcengineApiKey()) {
      toast.error('API key not configured');
      return;
    }

    if (!aiParams.prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setIsProcessing(true);
    
    try {
      // 转换图片为Base64
      const base64Image = await convertImageToBase64(originalImage);
      
      const result = await VolcengineService.editIcon({
        ...aiParams,
        image: base64Image,
      });

      if (result.success && (result.data?.data?.imageBase64 || result.data?.data?.imageUrl)) {
        setEditedImage(result.data.data.imageBase64 || result.data.data.imageUrl || '');
        
        toast.success('AI processing complete');
        setShowComparison(true);
        setComparisonOpen(true);
      } else {
        toast.error(result.error || 'AI processing failed');
      }
    } catch (error) {
      console.error('AI error:', error);
      toast.error('AI processing failed. Please try again');
    } finally {
      setIsProcessing(false);
    }
  };

  // 处理图片上传
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
    const maxSize = 10 * 1024 * 1024;
    if (!allowed.includes(file.type) || file.size > maxSize) {
      toast.error('Unsupported image format');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setOriginalImage(result);
      setUploadedFile({ name: file.name, size: `${(file.size / 1024 / 1024).toFixed(2)} MB` });
      setEditedImage('');
      setShowComparison(false);
      toast.success('uploaded successfully');
    };
    reader.readAsDataURL(file);
  };

  // 下载编辑后的图片
  const handleDownload = async () => {
    if (!editedImage) {
      toast.error('请先进行AI处理');
      return;
    }

    try {
      const options = {
        format: settings.downloadFormat,
        quality: settings.downloadQuality,
        filename: icon ? `${icon.trackName}_ai_edited` : 'ai_edited_icon',
      };

      await DownloadService.getInstance().downloadImage(
        editedImage,
        options.filename,
        options
      );
      
      toast.success('Download successful！');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed, please try again');
    }
  };

  const handleDownloadOriginal = async () => {
    if (!originalImage) {
      toast.error('请先上传图片');
      return;
    }
    try {
      const options = {
        format: settings.downloadFormat,
        quality: settings.downloadQuality,
        filename: icon ? `${icon.trackName}_original` : 'original_icon',
      };
      await DownloadService.getInstance().downloadImage(
        originalImage,
        options.filename,
        options
      );
      toast.success('Download successful！');
    } catch (error) {
      toast.error('Failed, please try again');
    }
  };

  // 重置到原始图片
  const handleReset = () => {
    setEditedImage('');
    setShowComparison(false);
    setAiParams(prev => ({
      ...prev,
      prompt: AI_STYLES[0].description,
      style: 'modern',
      strength: 0.7,
    }));
  };

  const pageTitle = 'AI Icon Editor – Edit and enhance app icons';
  const pageDesc = 'Edit app icons with AI, compare styles, and download results in multiple formats.';

  // 如果是独立页面模式，不显示模态框背景
  if (standalone) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDesc} />
        </Helmet>
        <div className="w-3/5 mx-auto px-0 py-8">
          {/* 头部 */}
          <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Sparkles className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">AI Editor</h1>
          </div>
          </div>

          {comparisonOpen && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                  <div className="text-lg font-semibold text-gray-900">Style Comparison</div>
                  <button onClick={() => setComparisonOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4 hidden">
                    <div className="space-x-2">
                      <button onClick={() => setComparisonMode('slider')} className={`px-3 py-1 rounded-lg border ${comparisonMode==='slider'?'bg-blue-600 text-white border-blue-600':'border-gray-300 text-gray-700'}`}>滑块</button>
                      <button onClick={() => setComparisonMode('side')} className={`px-3 py-1 rounded-lg border ${comparisonMode==='side'?'bg-blue-600 text-white border-blue-600':'border-gray-300 text-gray-700'}`}>并排</button>
                      <button onClick={() => setComparisonMode('overlay')} className={`px-3 py-1 rounded-lg border ${comparisonMode==='overlay'?'bg-blue-600 text-white border-blue-600':'border-gray-300 text-gray-700'}`}>叠加</button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button onClick={() => setZoom(Math.max(50, zoom-25))} className="px-2 py-1 rounded-lg border border-gray-300">-</button>
                      <span className="text-sm text-gray-700 w-12 text-center">{zoom}%</span>
                      <button onClick={() => setZoom(Math.min(300, zoom+25))} className="px-2 py-1 rounded-lg border border-gray-300">+</button>
                      {comparisonMode==='overlay' && (
                        <input type="range" min="0" max="1" step="0.05" value={overlayOpacity} onChange={(e)=>setOverlayOpacity(parseFloat(e.target.value))} className="w-32" />
                      )}
                      <button onClick={() => { setZoom(100); setSliderPos(50); setOverlayOpacity(0.5); }} className="px-2 py-1 rounded-lg border border-gray-300">重置</button>
                    </div>
                  </div>

                  {comparisonMode==='side' && (
                    <div className="flex w-full gap-4 bg-gray-50 rounded-lg shadow-lg p-4" style={{ transform: `scale(${zoom/100})`, transformOrigin: 'center' }}>
                      <div className="flex-1 relative flex items-center justify-center min-h-[360px]">
                        <img src={originalImage} alt="Original" className="w-full h-full object-contain rounded-lg" />
                        <div className="absolute top-3 left-3 bg-black/70 text-white text-sm px-2 py-1 rounded">Original</div>
                      </div>
                      <div className="flex-1 relative flex items-center justify-center min-h-[360px]">
                        <img src={editedImage} alt="Result" className="w-full h-full object-contain rounded-lg" />
                        <div className="absolute top-3 right-3 bg-purple-600/80 text-white text-sm px-2 py-1 rounded">Result</div>
                      </div>
                    </div>
                  )}

                  {comparisonMode==='slider' && (
                    <div ref={comparisonRef} className="relative w-full bg-gray-50 rounded-lg shadow-lg overflow-hidden" style={{ height: '360px', transform: `scale(${zoom/100})`, transformOrigin: 'center' }}>
                      <img src={originalImage} alt="Original" className="absolute inset-0 w-full h-full object-contain" />
                      <div className="absolute inset-0" style={{ width: `${sliderPos}%` }}>
                        <img src={editedImage} alt="Result" className="w-full h-full object-contain" />
                      </div>
                      <div
                        onMouseDown={(e)=>{
                          const rect = comparisonRef.current?.getBoundingClientRect();
                          if (!rect) return;
                          const onMove = (ev: MouseEvent) => {
                            const x = Math.min(rect.width, Math.max(0, ev.clientX - rect.left));
                            setSliderPos(Math.round((x / rect.width) * 100));
                          };
                          const onUp = () => {
                            window.removeEventListener('mousemove', onMove);
                            window.removeEventListener('mouseup', onUp);
                          };
                          window.addEventListener('mousemove', onMove);
                          window.addEventListener('mouseup', onUp);
                        }}
                        className="absolute top-0 h-full w-0.5 bg-blue-600 cursor-col-resize"
                        style={{ left: `calc(${sliderPos}% - 1px)` }}
                      ></div>
                    </div>
                  )}

                  {comparisonMode==='overlay' && (
                    <div className="relative w-full bg-gray-50 rounded-lg shadow-lg overflow-hidden" style={{ height: '360px', transform: `scale(${zoom/100})`, transformOrigin: 'center' }}>
                      <img src={originalImage} alt="Original" className="absolute inset-0 w-full h-full object-contain" />
                      <img src={editedImage} alt="Result" className="absolute inset-0 w-full h-full object-contain" style={{ opacity: overlayOpacity }} />
                      <div className="absolute top-3 left-3 bg-black/70 text-white text-sm px-2 py-1 rounded">Original</div>
                      <div className="absolute top-3 right-3 bg-purple-600/80 text-white text-sm px-2 py-1 rounded">Result</div>
                    </div>
                  )}
                </div>
                <div className="px-6 pb-6">
                  <div className="text-sm text-gray-600 mb-4 truncate">Prompt: {aiParams.prompt}</div>
                  <div className="flex justify-end gap-3">
                    <button onClick={handleDownload} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Download Result</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* 左侧控制面板 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              {/* 图片上传 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Image <span className="ml-2 text-xs text-gray-500">(PNG, JPG, SVG • up to 10MB)</span>
                </label>
                {originalImage && (
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
                      <img src={originalImage} alt="预览" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-sm text-gray-600">
                      {uploadedFile ? `${uploadedFile.name} · ${uploadedFile.size}` : 'Selected image'}
                    </div>
                    <button onClick={() => fileInputRef.current?.click()} className="ml-auto text-sm text-red-600">Reselect</button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Select Image File
                </button>
              </div>

              <div className="mb-6 space-y-4">
                <div className="flex">
                  <input
                    type="text"
                    value={aiParams.prompt}
                    onChange={(e) => setAiParams(prev => ({ ...prev, prompt: e.target.value }))}
                    placeholder="Enter a description to customize the icon style (optional)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg"
                  />
                  <select
                    value={aiParams.style as any}
                    onChange={(e) => setAiParams(prev => ({ ...prev, style: e.target.value as any, prompt: AI_STYLES.find(s=>s.value===e.target.value)?.description || prev.prompt }))}
                    className="w-40 px-3 py-2 border border-gray-300 border-l-0 rounded-r-lg"
                  >
                    {AI_STYLES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.values(AI_PROMPTS).map(p => (
                    <button
                      key={p}
                      onClick={() => setAiParams(prev => ({ ...prev, prompt: p }))}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="space-y-3">
                <button
                  onClick={handleAIProcess}
                  disabled={isProcessing || !aiParams.prompt.trim() || !originalImage}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate
                    </>
                  )}
                </button>

                {editedImage && (
                  <button
                    onClick={handleDownload}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Result
                  </button>
                )}

                {editedImage && (
                  <button
                    onClick={handleReset}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* 右侧预览区域 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Result</h3>
                  {editedImage && (
                    <button
                      onClick={() => setShowComparison(!showComparison)}
                      className="flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {showComparison ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {showComparison ? 'Hide Comparison' : 'Show Comparison'}
                    </button>
                  )}
                </div>

                {/* 图片预览区域 */}
                <div className="flex-1 flex items-center justify-center">
                  {!originalImage ? (
                    <div className="text-center text-gray-500">
                      <Palette className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>Please upload an image to start AI editing</p>
                    </div>
                  ) : showComparison && editedImage ? (
                    // 对比视图
                    <div className="flex w-full max-w-4xl gap-4 bg-gray-50 rounded-lg shadow-lg p-4">
                      <div className="flex-1 relative group flex items-center justify-center min-h-[360px]">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <img
                            src={originalImage}
                            alt="Original icon"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                          Original
                        </div>
                      </div>
                      
                      <div className="flex-1 relative group flex items-center justify-center min-h-[360px]">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <img
                            src={editedImage}
                            alt="AI result"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="absolute top-4 right-4 bg-purple-600 bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                          Result
                        </div>
                      </div>
                    </div>
                  ) : (
                    // 单图视图
                    <div className="relative group">
                      <div className="w-80 h-80 bg-gray-50 rounded-lg shadow-lg flex items-center justify-center">
                        <img
                          src={editedImage || originalImage}
                          alt="AI preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 模态框模式（原有的模态框代码）
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
      </Helmet>
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <Sparkles className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">AI Icon Editor</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-col lg:flex-row h-[calc(90vh-120px)]">
          {/* 左侧控制面板 */}
          <div className="lg:w-80 p-6 border-r bg-gray-50 overflow-y-auto">
            {/* 图片上传 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image <span className="ml-2 text-xs text-gray-500">(PNG, JPG, SVG • up to 10MB)</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Select Image File
              </button>
            </div>

            {/* AI风格选择 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Palette className="w-4 h-4 inline mr-2" />
                AI风格
              </label>
              <div className="space-y-2">
                {AI_STYLES.map((style) => (
                  <label key={style.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="aiStyle"
                      value={style.value}
                      checked={aiParams.style === style.value}
                      onChange={(e) => setAiParams(prev => ({ 
                        ...prev, 
                        style: e.target.value as any,
                        prompt: style.description
                      }))}
                      className="mr-3 text-purple-600"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{style.label}</div>
                      <div className="text-xs text-gray-500">{style.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* 处理强度 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Sliders className="w-4 h-4 inline mr-2" />
                processing intensity: {Math.round((aiParams.strength || 0.7) * 100)}%
              </label>
              <input
                type="range"
                min="0.1"
                max="1.0"
                step="0.1"
                value={aiParams.strength || 0.7}
                onChange={(e) => setAiParams(prev => ({ 
                  ...prev, 
                  strength: parseFloat(e.target.value)
                }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>slight</span>
                <span>moderate</span>
                <span>strongly</span>
              </div>
            </div>

            {/* 自定义提示词 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom description
              </label>
              <textarea
                value={aiParams.prompt}
                onChange={(e) => setAiParams(prev => ({ ...prev, prompt: e.target.value }))}
                placeholder="Describe the AI processing effect you want..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none h-20 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-2">Quick Select:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(AI_PROMPTS).slice(0, 4).map(([key, prompt]) => (
                    <button
                      key={key}
                      onClick={() => setAiParams(prev => ({ ...prev, prompt }))}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="space-y-3">
              <button
                onClick={handleAIProcess}
                disabled={isProcessing || !aiParams.prompt.trim()}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    AI processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start AI processing
                  </>
                )}
              </button>

              {editedImage && (
                <button
                  onClick={handleDownload}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download the edited results
                </button>
              )}

              {editedImage && (
                <button
                  onClick={handleReset}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* 右侧预览区域 */}
          <div className="flex-1 p-6 bg-gray-100">
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">preview</h3>
                {editedImage && (
                  <button
                    onClick={() => setShowComparison(!showComparison)}
                    className="flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {showComparison ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                    {showComparison ? 'hidden' : 'display'}
                  </button>
                )}
              </div>

              {/* 图片预览区域 */}
              <div className="flex-1 flex items-center justify-center">
                {showComparison && editedImage ? (
                  // 对比视图
                  <div className="flex w-full max-w-4xl h-96 bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="flex-1 relative group">
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                        <img
                          src={originalImage}
                          alt="原始图标"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                        原始
                      </div>
                    </div>
                    <div className="w-px bg-gray-300"></div>
                    <div className="flex-1 relative group">
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                        <img
                          src={editedImage}
                          alt="AI处理后"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="absolute top-4 right-4 bg-purple-600 bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                        AI处理
                      </div>
                    </div>
                  </div>
                ) : (
                  // 单图视图
                  <div className="relative group">
                    <div className="w-80 h-80 bg-white rounded-lg shadow-lg flex items-center justify-center">
                      <img
                        src={editedImage || originalImage}
                        alt={icon?.trackName || 'AI处理预览'}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    {editedImage && (
                      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-sm text-gray-600">
                        AI处理效果预览
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 图标信息 */}
              {icon && (
                <div className="mt-6 p-4 bg-white rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">{icon.trackName}</h4>
                  <p className="text-sm text-gray-600 mb-1">开发者: {icon.artistName}</p>
                  <p className="text-sm text-gray-600">
                    区域: {icon.country.toUpperCase()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
